<template>
  <table class="table align-items-center mb-0">
    <thead>
      <tr>
        <th class="text-sm">Port</th>
        <th class="text-sm">TX Power (dBm)</th>
        <th class="text-sm">Current (mA)</th>
        <th class="text-sm">Voltage (V)</th>
        <th class="text-sm">Temperature (°C)</th>
        <th class="text-sm">Status</th>
      </tr>
    </thead>
    <tbody>
      <template v-if="loading">
        <tr>
          <td colspan="6">
            <Skalaton />
          </td>
        </tr>
      </template>
      <template v-else-if="hasPonData">
        <tr
          v-for="(port, index) in ponDataArray"
          :key="index"
        >
          <td class="text-sm font-weight-bold">{{ port['Interface Name'] || port.interface_name || port.port || 'N/A' }}</td>
          <td class="text-sm" :class="getTxPowerClass(port['TxPower(dBm)'] || port.tx_power || port.txpower)">
            {{ formatPower(port['TxPower(dBm)'] || port.tx_power || port.txpower) }}
          </td>
          <td class="text-sm">{{ formatCurrent(port['Current(mA)'] || port.current || port.bias_current) }}</td>
          <td class="text-sm">{{ formatVoltage(port['Voltage(V)'] || port.voltage) }}</td>
          <td class="text-sm" :class="getTemperatureClass(port['Temperaturer(degree)'] || port.temperature || port.temp)">
            {{ formatTemperature(port['Temperaturer(degree)'] || port.temperature || port.temp) }}
          </td>
          <td class="text-sm">
            <span
              class="badge"
              :class="getStatusClass(port)"
              style="text-transform: uppercase; color: rgb(255 249 249) !important;"
            >
              {{ getStatusText(port) }}
            </span>
          </td>
        </tr>
      </template>
      <template v-else>
        <tr>
          <td colspan="6" class="text-center">
            <Skalaton />
            <!-- No PON port data available -->
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<script>
import Skalaton from '@/components/Skalaton.vue';

export default {
  name: 'BdcomEponPonTable',
  components: {
    Skalaton
  },
  props: {
    bdcomEponPonInfo: {
      type: [Array, Object],
      default: () => []
    },
    loading: Boolean
  },
  computed: {
    hasPonData() {
      if (Array.isArray(this.bdcomEponPonInfo)) {
        return this.bdcomEponPonInfo.length > 0;
      }
      if (this.bdcomEponPonInfo && typeof this.bdcomEponPonInfo === 'object') {
        // Check if it has ports array or similar structure
        if (this.bdcomEponPonInfo.ports && Array.isArray(this.bdcomEponPonInfo.ports)) {
          return this.bdcomEponPonInfo.ports.length > 0;
        }
        // Check if it's an object with data array
        if (this.bdcomEponPonInfo.data && Array.isArray(this.bdcomEponPonInfo.data)) {
          return this.bdcomEponPonInfo.data.length > 0;
        }
        // Check if object itself has port-like properties
        return Object.keys(this.bdcomEponPonInfo).length > 0;
      }
      return false;
    },
    ponDataArray() {
      if (Array.isArray(this.bdcomEponPonInfo)) {
        return this.bdcomEponPonInfo;
      }
      if (this.bdcomEponPonInfo && typeof this.bdcomEponPonInfo === 'object') {
        if (this.bdcomEponPonInfo.ports && Array.isArray(this.bdcomEponPonInfo.ports)) {
          return this.bdcomEponPonInfo.ports;
        }
        if (this.bdcomEponPonInfo.data && Array.isArray(this.bdcomEponPonInfo.data)) {
          return this.bdcomEponPonInfo.data;
        }
        // If it's an object, convert to array
        return [this.bdcomEponPonInfo];
      }
      return [];
    }
  },
  methods: {
    formatPower(value) {
      if (value === null || value === undefined || value === 'N/A' || value === '0.0' || value === '') return 'N/A';
      return `${value} dBm`;
    },
    
    formatCurrent(value) {
      if (value === null || value === undefined || value === 'N/A' || value === '0.0' || value === '') return 'N/A';
      return `${value} mA`;
    },
    
    formatVoltage(value) {
      if (value === null || value === undefined || value === 'N/A' || value === '0.0' || value === '') return 'N/A';
      return `${value} V`;
    },
    
    formatTemperature(value) {
      if (value === null || value === undefined || value === 'N/A' || value === '0.0' || value === '') return 'N/A';
      return `${value}°C`;
    },
    
    getTxPowerClass(value) {
      if (value === null || value === undefined || value === 'N/A' || value === '0.0' || value === '') return 'text-muted';
      
      const powerValue = parseFloat(value);
      if (isNaN(powerValue) || powerValue === 0) return 'text-muted';
      // EPON TX power should be positive (typically 2-9 dBm)
      if (powerValue >= 2 && powerValue <= 9) return 'text-success'; // Normal range
      if (powerValue > 0 && powerValue < 2) return 'text-warning'; // Low but working
      return 'text-danger'; // Out of range
    },
    
    getTemperatureClass(value) {
      if (value === null || value === undefined || value === 'N/A' || value === '0.0' || value === '') return 'text-muted';
      
      const tempValue = parseFloat(value);
      if (isNaN(tempValue) || tempValue === 0) return 'text-muted';
      if (tempValue < 50) return 'text-success'; // Normal temperature
      if (tempValue < 60) return 'text-warning'; // Warning temperature
      return 'text-danger'; // Critical temperature
    },
    
    getStatusClass(port) {
      // Check if port has valid TX power
      const txPower = port['TxPower(dBm)'] || port.tx_power || port.txpower;
      if (txPower && txPower !== 'N/A' && txPower !== '0.0' && parseFloat(txPower) > 0) {
        return 'bg-success'; // Active and working
      }
      return 'bg-secondary'; // Inactive
    },
    
    getStatusText(port) {
      // Check if port has valid TX power
      const txPower = port['TxPower(dBm)'] || port.tx_power || port.txpower;
      if (txPower && txPower !== 'N/A' && txPower !== '0.0' && parseFloat(txPower) > 0) {
        return 'Active';
      }
      return 'Inactive';
    }
  }
}
</script>

